import { useCallback, useEffect, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, EmptyState, Eyebrow, Field, IconButton, LoadingRows, Notice,
  PageHeader, Panel, Table, TableCell, TableHead, TableHeader, TableRow, TextAreaField,
} from "../components/ui.jsx";

const EMPTY_COMPANY = {
  name: "", domain: "", website_url: "", linkedin_url: "", description: "", lifecycle: "active",
};

function AddCompany({ onCreated, onCancel }) {
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [error, setError] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function save(createAnyway = false) {
    setBusy(true);
    setError("");
    try {
      const result = await api.createCompany({
        ...form,
        domain: form.domain || null,
        website_url: form.website_url || null,
        linkedin_url: form.linkedin_url || null,
        description: form.description || null,
        create_anyway: createAnyway,
      });
      onCreated(result.company, result.deduplicated);
    } catch (requestError) {
      if (requestError.code === "possible_duplicate") setCandidates(requestError.details?.candidates || []);
      else setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <form className="grid gap-5 lg:grid-cols-2" onSubmit={(event) => { event.preventDefault(); save(false); }}>
        <div className="col-span-full flex items-center justify-between"><div className="grid gap-1"><Eyebrow>Canonical record</Eyebrow><h2 className="text-xl font-bold">Add company</h2></div><IconButton label="Close company form" onClick={onCancel}>×</IconButton></div>
        {error && <div className="col-span-full"><Notice>{error}</Notice></div>}
        <Field label="Company name" value={form.name} onChange={set("name")} autoFocus required />
        <Field label="Domain" hint="strong duplicate key" placeholder="acme.com" value={form.domain} onChange={set("domain")} />
        <Field label="Website" hint="optional" type="url" placeholder="https://acme.com" value={form.website_url} onChange={set("website_url")} />
        <Field label="LinkedIn" hint="optional" type="url" placeholder="https://linkedin.com/company/acme" value={form.linkedin_url} onChange={set("linkedin_url")} />
        <div className="col-span-full"><TextAreaField label="Description" hint="optional" value={form.description} onChange={set("description")} /></div>
        {candidates.length > 0 && (
          <div className="col-span-full grid gap-3 rounded-xl bg-amber-500/10 p-4">
            <div><strong className="text-sm text-amber-700">Possible duplicate</strong><p className="mt-1 text-xs leading-5 text-muted-foreground">Open the existing company, or confirm that this is a distinct record.</p></div>
            <div className="grid gap-1.5">{candidates.map((candidate) => <button className="flex min-h-11 cursor-pointer items-center justify-between rounded-lg bg-card px-3 text-left text-xs shadow-soft transition-colors hover:bg-primary-50" type="button" key={candidate.id} onClick={() => onCreated(candidate, true)}><strong>{candidate.name}</strong><span className="text-muted-foreground">{candidate.domain || "No domain"} →</span></button>)}</div>
            <Button variant="secondary" className="justify-self-start" disabled={busy} onClick={() => save(true)}>Create as a separate company</Button>
          </div>
        )}
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Checking…" : "Add company"}</Button></div>
      </form>
    </Panel>
  );
}

export default function CompaniesPage({ onOpenCompany }) {
  const [state, setState] = useState({ loading: true, companies: [], error: "" });
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async (query = search) => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await api.companies({ lifecycle: "all", ...(query ? { search: query } : {}) });
      setState({ loading: false, companies: data.companies, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [search]);

  useEffect(() => { load(""); }, []);

  function created(company, deduplicated) {
    setAdding(false);
    setMessage(deduplicated ? `Opened existing company: ${company.name}` : `Added ${company.name}`);
    load("");
    onOpenCompany(company.id);
  }

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Company memory" title="Companies" description="Canonical organizations shared across research, lists, sales work, and agents." action={!adding && <Button variant="primary" onClick={() => setAdding(true)}>Add company</Button>} />
      {message && <Notice tone="info">{message}</Notice>}
      {adding && <AddCompany onCreated={created} onCancel={() => setAdding(false)} />}
      <form className="grid gap-3 rounded-xl bg-card p-4 shadow-soft sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end" onSubmit={(event) => { event.preventDefault(); load(search); }}>
        <Field label="Search companies" placeholder="Name or domain" value={search} onChange={(event) => setSearch(event.target.value)} />
        <Button className="self-end">Search</Button>
      </form>
      {state.error && <Notice>{state.error} <Button variant="ghost" className="ml-2 min-h-8 px-2" onClick={() => load()}>Retry</Button></Notice>}
      <section className="overflow-hidden rounded-xl bg-card shadow-soft">
        {state.loading ? <LoadingRows count={5} /> : state.companies.length === 0 ? <EmptyState title="No companies yet" description="Add the first canonical company. Research, people and lists will attach to this record rather than create copies." /> : (
          <Table>
            <TableHead><TableHeader>Company</TableHeader><TableHeader>Domain</TableHeader><TableHeader>People</TableHeader><TableHeader>Memory</TableHeader><TableHeader>Status</TableHeader><TableHeader /></TableHead>
            <tbody>{state.companies.map((company) => (
              <TableRow className="cursor-pointer" key={company.id} onClick={() => onOpenCompany(company.id)}>
                <TableCell><strong className="block text-[13px]">{company.name}</strong><small className="mt-1 block max-w-[36ch] truncate text-[11px] text-muted-foreground">{company.description || "No description yet"}</small></TableCell>
                <TableCell className="text-muted-foreground">{company.domain || "—"}</TableCell>
                <TableCell>{company.people_count}</TableCell>
                <TableCell>{company.facts_count}</TableCell>
                <TableCell><Badge status={company.lifecycle}>{company.lifecycle}</Badge></TableCell>
                <TableCell className="text-right text-muted-foreground">→</TableCell>
              </TableRow>
            ))}</tbody>
          </Table>
        )}
      </section>
    </div>
  );
}
